/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";

import BreadCrumb from "../../../components/breadCrumb";
import Loader from "../../../components/PageLoader";
import ReactHookFormDropdown from "../../../components/forms/react-hook-dropdown";
import FormInput from "../../../components/forms/form-Input";

import { ESCALATION_LEVELS } from "../constants";
import { Paths } from "../../../routes/paths";

import {
  useLookupAssignmentGroup,
  useLookupRegions,
} from "../../../services/lookups-mutations";
import { useAddEscalationMatrix } from "../../../services/escalation-matrix";

import { Button, Flex } from "@chakra-ui/react";
import EmailChipInput from "components/EmailChipInput";
import ReactHookFormMultiSelect from "components/forms/react-hook-multiselect";

const ACTIVITY_TYPES = [
  { id: "Customer Service", text: "Customer Service" },
  { id: "ETISALAT Third Party", text: "ETISALAT Third Party" },
  { id: "Facilities Request", text: "Facilities Request" },
  { id: "TGM Remote Support", text: "TGM Remote Support" },
  { id: "Unusual Event", text: "Unusual Event" },
  { id: "Customer Complaints", text: "Customer Complaints" },
  { id: "Voice Of Staff", text: "Voice Of Staff" },
  { id: "Incident - Standard", text: "Incident - Standard" },
  { id: "Incident - Emergency", text: "Incident - Emergency" },
  { id: "Panic Alert Incident", text: "Panic Alert Incident" },
  { id: "Marketing", text: "Marketing" },
  { id: "Missed And Delayed Services", text: "Missed And Delayed Services" },
  { id: "TGSI Helpdesk", text: "TGSI Helpdesk" },
];

/* ---------------- VALIDATION ---------------- */
const FORM_VALIDATION = {
  name: { required: "Please enter Name" },
  designation: { required: "Please enter Designation" },
  escalationLevel: {
    required: "Please select Escalation Level",
  },
  assignmentGroup: { required: "Please select Assignment Group" },
  region: { validate: (v) => v?.length > 0 || "Please select Region(s)" },
  activities: { validate: (v) => v?.length > 0 || "Please select Activity" },
  emails: {
    validate: (v) => v?.length > 0 || "Please enter at least one email address",
  },
};

/* ================= COMPONENT ================= */
export default function AddEscalationMatrix() {
  const navigate = useNavigate();

  /* ---------------- LOOKUPS ---------------- */
  const { mutate: getAssignmentGroup, data: assignmentGroupRes } =
    useLookupAssignmentGroup();
  const { mutate: getRegions, data: regionsList } = useLookupRegions();

  /* ---------------- FORM ---------------- */
  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      designation: "",
      escalationLevel: null,
      assignmentGroup: null,
      region: [],
      activities: [],
      emails: [],
      isActive: true,
    },
  });

  /* ---------------- ADD ESCALATION MATRIX ---------------- */
  const {
    mutate: addMatrix,
    isPending: saving,
    isSuccess,
  } = useAddEscalationMatrix();

  useEffect(() => {
    getAssignmentGroup();
    getRegions("UAE");
  }, []);

  useEffect(() => {
    if (isSuccess) navigate(Paths.escalationMatrix);
  }, [isSuccess]);

  /* ---------------- HELPERS ---------------- */
  const prependAllOption = (list, key = "id", textKey = "name") => {
    if (!list) return [];
    return [{ [key]: "All", [textKey]: "All" }, ...list];
  };

  const handleAllSelection = (values, allKey = "id") => {
    const hasAll = values.some((v) => v[allKey] === "All");
    if (!hasAll) return values;
    return values.length > 1
      ? values.filter((v) => v[allKey] !== "All")
      : values;
  };

  const normalizedRegions = useMemo(
    () =>
      prependAllOption(regionsList?.data || [], "id", "name").map((r) => ({
        ...r,
        id: String(r.id ?? r.name ?? ""),
        name: String(r.name ?? ""),
      })),
    [regionsList],
  );

  const normalizedActivities = useMemo(
    () =>
      prependAllOption(ACTIVITY_TYPES || [], "id", "text").map((a) => ({
        id: String(a.id ?? a.text ?? ""),
        text: String(a.text ?? ""),
      })),
    [],
  );
  const handleCancel = () => {
    reset();
    navigate(Paths.escalationMatrix);
  };

  /* ---------------- SAVE ---------------- */
  const onSave = (data) => {
    const payload = {
      name: data.name,
      designation: data.designation,
      escalationLevel: [data.escalationLevel],
      assignmentGroup: data.assignmentGroup,
      region: data.region.map((r) => r.id),
      activityTypes: data.activities.map((a) => a.id),
      emails: data.emails,
      // .split(",")
      // .map((e) => e.trim())
      // .filter(Boolean),

      isActive: data.isActive,
    };

    addMatrix(payload);
  };

  /* ================= RENDER ================= */
  return (
    <>
      {saving ? <Loader /> : null}

      <div style={{ maxWidth: "100%", margin: "0 auto", padding: "20px" }}>
        <BreadCrumb
          links={[
            { text: "Escalation Matrix", link: Paths.escalationMatrix },
            { text: "Add Escalation Matrix", isCurrentPage: true },
          ]}
        />

        <div className="bg-white p-6 rounded shadow">
          <h2 className="text-lg font-semibold mb-4">Add Escalation Matrix</h2>

          <form onSubmit={handleSubmit(onSave)} className="space-y-4">
            {/* Active Checkbox */}
            <Controller
              control={control}
              name="isActive"
              render={({ field }) => (
                <label className="flex items-center space-x-2 mb-2">
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  <span>Active</span>
                </label>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <FormInput
                control={control}
                name="name"
                label="Group Name"
                errors={errors}
                rules={FORM_VALIDATION.name}
                isRequired
              />

              <FormInput
                control={control}
                name="designation"
                label="Descriptions"
                errors={errors}
                rules={FORM_VALIDATION.designation}
                isRequired
              />
              <ReactHookFormDropdown
                control={control}
                name="escalationLevel"
                label="Escalation Level"
                errors={errors}
                rules={FORM_VALIDATION.escalationLevel}
                isRequired
                filterable
                data={ESCALATION_LEVELS.map((e) => ({
                  value: String(e.id),
                  label: String(e.text),
                }))}
                id="id"
                text="text"
                placeholder="Select Escalation Level"
              />

              <ReactHookFormDropdown
                control={control}
                name="assignmentGroup"
                label="Assignment Group"
                errors={errors}
                rules={FORM_VALIDATION.assignmentGroup}
                isRequired
                filterable
                data={prependAllOption(
                  assignmentGroupRes?.data,
                  "name",
                  "name",
                ).map((item) => ({
                  value: String(item.name),
                  label: String(item.name),
                }))}
                id="name"
                text="name"
                placeholder="Select a Value"
              />

              <Controller
                control={control}
                name="emails"
                rules={FORM_VALIDATION.emails}
                render={({ field }) => (
                  <EmailChipInput
                    label="Email"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.emails?.message}
                  />
                )}
              />

              <ReactHookFormMultiSelect
                label="Activity Types"
                name="activities"
                control={control}
                errors={errors}
                isRequired
                filterable
                data={normalizedActivities}
                id="id"
                text="text"
                rules={FORM_VALIDATION.activities}
                placeholder="Select a Value"
                onChange={(value) => handleAllSelection(value, "id")}
              />

              <ReactHookFormMultiSelect
                label="Regions"
                name="region"
                control={control}
                errors={errors}
                isRequired
                filterable
                data={normalizedRegions}
                id="id"
                text="name"
                rules={FORM_VALIDATION.region}
                placeholder="Select a Value"
                onChange={(value) => handleAllSelection(value, "id")}
              />
            </div>

            <Flex justify="flex-end" mt="30px" gap="10px">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">Add Escalation Matrix</Button>
            </Flex>
          </form>
        </div>
      </div>
    </>
  );
}
